
import { v4 as uuidv4 } from "uuid";
import { 
  FloatingFeature,
  Feature 
} from "@models";
import { 
  CharacterASIBaseFeature,
  CharacterLanguageFeature,
  CharacterFeat,
  CharacterEldritchInvocation,
  CharacterFightingStyle,
  CharacterPactBoon,
  CharacterConditionFeature,
  CharacterSpecialFeature,
  CharacterSpellcasting,
  Character,
  ASIBaseFeature,
  LanguageFeature,
  CharacterProficiency,
  BasicCreatureInstance,
  CharacterSubclass,
  CharacterSubrace,
  FeatureAncestry,
  SummonInstance,
  CharacterFeatureBase,
  Proficiency,
  Selectable,
  CharacterBonusSpells,
  BonusSpells,
  ComplexCreatureInstance,
} from "@dndModels";

import CommonUtilities from "@utilities/common_utilities";
import { CommonUtilitiesClass } from "@utilities/common_utilities_class";
import { CreatureFBType, FeatureType } from "@dndModels/Types";
import DandDCharacterUtilities from "@dndUtilities/char_utilities";
// import { CharacterKnowledge } from "../character_feature_source/CharacterKnowledge";

const char_util = DandDCharacterUtilities.getInstance();

/**
 * This represents a feature 
 * (either via race, background, class, or subclass)
 * which the character has.  
 * The FeatureBase is what it is based on, but the 
 * difference is that here we will have values for the choices.
 */
export class CharacterFeature {
  feature_id = "";
  true_id = "";
  feature_type: FeatureType = "";
  feature: Feature | null = null;
  name = "";
  special_resource = "";
  special_resource_max = "";
  // options holds things that can vary for the feature. 
  // for ASI it will have objects with the amounts and the selected ability scores
  // for Spellbook it will have objects with spell_id's, the level it was learned at, and whether it was learned at level up or from someting else (training, scroll, or spellbook)
  feature_options: any[] = []; 
  source_true_id = "";
  source_id = "";
  source_type = "";
  source_name = "";
  // root_id: string = "";
  // root_type: string = "";
  // root_name: string = "";
  ancestries: FeatureAncestry[] = [];
  source_books: { type: string, id: string, third_party: boolean }[] = [];
  
  item_id = "";
  item_name = "";
  cfb_id = "";
  cfb_name = "";
  replaces_cfb_on_source_id = "";
  replaces_cfb_on_source_type = "";
  replaces_cfb_on_source_name = "";
  replaces_cfb_on_item_id = "";
  replaces_cfb_on_item_name = "";
  cfb_level = 0;

  constructor(obj?: any, log_it = false) {
    this.true_id = uuidv4().toString();
    if (obj) {
      if (obj.feature_id) {
        this.true_id = obj.true_id;
        this.feature_id = obj.feature_id;
      } else if (obj.true_id) {
        this.feature_id = obj.true_id;
      }
      this.name = obj.name ? `${obj.name}` : "";
      this.feature_type = obj.feature_type ? obj.feature_type : "";
      if ((this.feature_type as string) === "Spell Book") {
        this.feature_type = "Spellbook";
      } else if (this.feature_type.includes("Proficienc")) {
        this.feature_type = "Proficiencies";
      }
      this.special_resource = obj.special_resource ? `${obj.special_resource}` : "";
      this.special_resource_max = obj.special_resource_max ? `${obj.special_resource_max}` : "";
      this.feature_options = [];
      if (obj.feature_options && obj.feature_options.length > 0) {
        for (const opt of obj.feature_options) {
          switch (this.feature_type) {
            case "Language":
              this.feature_options.push(new CharacterLanguageFeature(opt));
            break;
            case "Ability Score Improvement":
              this.feature_options.push(new CharacterASIBaseFeature(opt));
            break;
            case "Condition": // It's the ids of the specific skills they choose
              this.feature_options.push(new CharacterConditionFeature(opt));
            break;
            case "Allowed Conditions": // No need to do anything here
            break;
            case "Specific Special Feature":
            case "Special Feature":
            case "Special Feature Choices":
              this.feature_options.push(new CharacterSpecialFeature(opt));
            break;
            case "Feat":
              this.feature_options.push(new CharacterFeat(opt));
            break;
            case "Selectable":
              this.feature_options.push(opt as string);
            break;
            case "Eldritch Invocations":
              this.feature_options.push(new CharacterEldritchInvocation(opt));
            break;
            case "Weapon Mastery":
              this.feature_options.push(opt);
            break;
            case "Fighting Style":
              this.feature_options.push(new CharacterFightingStyle(opt));
            break;
            case "Pact Boon":
              this.feature_options.push(new CharacterPactBoon(opt));
            break;
            case "Subclass":
              this.feature_options.push(new CharacterSubclass(opt));
            break;
            case "Subrace":
              this.feature_options.push(new CharacterSubrace(opt));
            break;
            case "Sense":
              // this.feature_options.push(new CharacterSense(opt));
            break;
            case "Proficiencies":
            case "Expertise":
              // debugger;
              // This is unnecessary as it should already conform to the interface,
              // but having it here might be useful for testing
              const char_prof: CharacterProficiency = {
                prof_feature_id: opt.prof_feature_id,
                expertise_feature_id: opt.expertise_feature_id,
                proficiency_id: opt.proficiency_id,
                proficiency_level: opt.proficiency_level,
                type: opt.type,
                extra_bonus: opt.extra_bonus ? opt.extra_bonus : 0
              };
              if (["Gaming Set", "Artisan's Tools", "Musical Instrument", "Specialty Tool", "Other Tool"].includes(char_prof.proficiency_id)) {
                char_prof.proficiency_id = "";
              }
              this.feature_options.push(char_prof);
            break;
            case "Characteristic":
              this.feature_options.push(opt);
            break;
            case "Bonus Spells":
              this.feature_options.push(new CharacterBonusSpells(opt));
            break;
            default:
              if (["Spellbook","Bonus Spells","Spellcasting","Spell List","Ritual Casting",
                "Cantrips","Spells","Mystic Arcanum","Spell Mastery"].includes(this.feature_type)) {
                this.feature_options.push(new CharacterSpellcasting(opt));
              } else {
                this.feature_options.push(opt);
              }
            break;
          }
        }
        if (log_it) {
          console.log(obj.feature_options);
          console.log(this.feature_type);
          console.log(this.feature_options);
        }
      }
      this.source_true_id = obj.source_true_id ? obj.source_true_id : "";
      this.source_id = obj.source_id ? obj.source_id : "";
      this.feature = obj.feature ? new Feature(obj.feature) : null;
    }
    this.common_util = CommonUtilities.getInstance();
  }

  common_util: CommonUtilitiesClass;

  toDBObj(): any {
    let feature_options: any[] = [];
    for (const opt of this.feature_options) {
      try {
        switch (this.feature_type) {
          case "Language":
            feature_options.push((opt as CharacterLanguageFeature).toDBObj());
          break;
          case "Ability Score Improvement":
            feature_options.push((opt as CharacterASIBaseFeature).toDBObj());
          break;
          case "Condition":
            feature_options.push((opt as CharacterConditionFeature).toDBObj());
          break;
          case "Allowed Conditions": // No need to do anything here
          break;
          case "Specific Special Feature":
          case "Special Feature":
          case "Special Feature Choices":
            feature_options.push((opt as CharacterSpecialFeature).toDBObj());
          break;
          case "Sense":
            // for (let i = 0; i < this.feature_options.length; i++) {
            //   feature_options.push((this.feature_options[i] as CharacterSense).toDBObj());
            // }
          break;
          case "Proficiencies":
          case "Expertise":
            feature_options.push({...opt}); // It's a CharacterProficiency, which is an interface
          break;
          case "Characteristic":
            feature_options.push(opt);
          break;
          case "Feat":
            feature_options.push((opt as CharacterFeat).toDBObj());
          break;
          // case "Selectable":
          //   feature_options.push(opt as string);
          // break;
          case "Eldritch Invocations": // It's the id of the specific feat they choose
            feature_options.push((opt as CharacterEldritchInvocation).toDBObj());
          break;
          case "Weapon Mastery":
            // debugger;
            feature_options.push(opt);
          break;
          case "Fighting Style": // It's the id of the specific feat they choose
            feature_options.push((opt as CharacterFightingStyle).toDBObj());
          break;
          case "Pact Boon": // It's the id of the specific feat they choose
            feature_options.push((opt as CharacterPactBoon).toDBObj());
          break;
          case "Subclass": 
            // Make it so when setting subclass to null it doesn't set opt to null, but just clears out the subclass from the char subclass
            if (opt !== null) {
              feature_options.push((opt as CharacterSubclass).toDBObj());
            }
          break;
          case "Subrace": 
            // Make it so when setting subclass to null it doesn't set opt to null, but just clears out the subclass from the char subclass
            if (opt !== null) {
              feature_options.push((opt as CharacterSubrace).toDBObj());
            }
          break;
          case "Bonus Spells":
            feature_options.push((opt as CharacterBonusSpells).toDBObj());
          break;
          default:
            feature_options = this.feature_options;
          break;
        }
      } catch (ex) {
        console.error(ex);
      }
    }
    let feature: any = null;
    if (this.feature) {
      feature = this.feature.toDBObj();
    }
    return {
      true_id: this.true_id,
      feature_id: this.feature_id,
      name: this.name,
      feature_type: this.feature_type,
      special_resource: this.special_resource,
      special_resource_max: this.special_resource_max,
      feature_options,
      feature,
      source_id: this.source_id,
      source_true_id: this.source_true_id
    };
  }

  get use_source_id(): string {
    if (this.replaces_cfb_on_source_id === "") {
      return this.source_id;
    } else {
      return this.replaces_cfb_on_source_id;
    }
  }

  get use_source_type(): string {
    if (this.replaces_cfb_on_source_type === "") {
      return this.source_type;
    } else {
      return this.replaces_cfb_on_source_type;
    }
  }

  get use_source_name(): string {
    if (this.replaces_cfb_on_source_name === "") {
      return this.source_name;
    } else {
      return this.replaces_cfb_on_source_name;
    }
  }

  get use_item_id(): string {
    if (this.replaces_cfb_on_item_id === "") {
      return this.item_id;
    } else {
      return this.replaces_cfb_on_item_id;
    }
  }

  get use_item_name(): string {
    if (this.replaces_cfb_on_item_name === "") {
      return this.item_name;
    } else {
      return this.replaces_cfb_on_item_name;
    }
  }

  get description(): string {
    if (this.floating_feature) {
      return this.floating_feature.description;
    } else {
      return "";
    }
  }

  get root(): FeatureAncestry | undefined {
    return this.ancestries.find(o => o.parent_id === "");
  }

  get root_id(): string {
    const root = this.root;
    if (root) {
      return root.source_id;
    } else {
      return this.use_source_id;
    }
  }

  get root_name(): string {
    const root = this.root;
    if (root) {
      return root.name;
    } else {
      return this.use_source_name;
    }
  }

  get root_type(): string {
    const root = this.root;
    if (root) {
      return root.type;
    } else {
      return this.source_type;
    }
  }

  get source_book_ids(): string[] {
    return this.source_books.map(o => o.id);
  }

  get source_book_types(): string[] {
    return this.source_books.map(o => o.type);
  }

  get third_party(): boolean {
    return this.source_books.filter(o => !o.third_party).length === 0;
  }

  creature_fb_type: CreatureFBType | "" = "";
  copyFeature(copyMe: Feature, 
    char: Character | ComplexCreatureInstance | null,
    parent: CharacterFeatureBase | BasicCreatureInstance | SummonInstance | ComplexCreatureInstance | null = null,
    log_it = false): void {
    this.feature = copyMe;
    this.feature_id = copyMe.true_id;
    // log_it = this.name === "ASI INT, WIS, or CHA 1";
    if (log_it) {
      console.log(copyMe.clone());
      console.log(this.clone());
      if (this.feature_options[0] instanceof CharacterASIBaseFeature) {
        const cabf = this.feature_options[0];
        for (const caf of cabf.asi_features) {
          console.log(caf.selected_option);
        }
      }
    }
    this.feature_type = copyMe.feature_type;
    if (parent instanceof CharacterFeatureBase) {
      this.source_true_id = parent.source_true_id;
      this.source_id = parent.use_source_id;
      this.source_name = parent.use_source_name;
      this.source_type = parent.use_source_type;
      // this.root_id = parent.root_id;
      // this.root_name = parent.root_name;
      // this.root_type = parent.root_type;
      this.ancestries = [...parent.ancestries.map(o => { return {...o}; })];
      this.item_id = parent.use_item_id;
      this.item_name = parent.use_item_name;
      this.creature_fb_type = parent.creature_fb_type;
    } else if (parent instanceof BasicCreatureInstance) {
      this.source_id = parent.true_id;
      this.source_name = parent.name;
      this.source_type = "Creature";
      // this.root_id = parent.true_id;
      // this.root_name = parent.name;
      // this.root_type = "Creature";
    } else if (parent instanceof SummonInstance) {
      this.source_id = parent.source_true_id;
      this.source_name = parent.source_name;
      this.source_type = parent.source_type;
      // this.root_id = parent.root_id;
      // this.root_name = parent.root_name;
      // this.root_type = parent.root_type;
    }
    // if (log_it) {
    //   console.log(this);
    //   console.log(this.feature_options);
    //   console.log(this.feature_options[0]);
    //   console.log(copyMe);
    // }
    if (copyMe.floating_feature) {
      this.name = copyMe.floating_feature.name;
      if (copyMe.feature_type === "Language") {
        if (this.feature_options.length === 0) {
          const language_feature = copyMe.floating_feature as LanguageFeature;
          const char_language_feature = new CharacterLanguageFeature();
          char_language_feature.copyLanguageFeature(language_feature);
          this.feature_options.push(char_language_feature);
        }
      } else if (copyMe.feature_type === "Ability Score Improvement") {
        if (log_it) {
          console.log(copyMe.clone());
          console.log(this.clone());
          if (this.feature_options[0] instanceof CharacterASIBaseFeature) {
            const cabf = this.feature_options[0];
            console.log(cabf.selected_choice_id);
            for (const caf of cabf.asi_features) {
              console.log(caf.selected_option);
            }
          }
        }
        const rebuild = () => {
          const asi = copyMe.floating_feature as ASIBaseFeature;
          const char_asi = new CharacterASIBaseFeature();
          char_asi.copyASIBaseFeature(asi);
          // char_asi.id = 0;
          this.feature_options = [char_asi];
        }
        if (this.feature_options.length === 0) {
          rebuild();
        } else {
          const cabf = this.feature_options[0] as CharacterASIBaseFeature;
          const abf = copyMe.floating_feature as ASIBaseFeature;
          cabf.asi_base_feature = abf;
          const choice = cabf.asi_base_feature.choices.length === 1 ? cabf.asi_base_feature.choices[0] : cabf.asi_base_feature.choices.find(o => o.true_id === cabf.selected_choice_id);
          if (choice && choice.asi_features.length === cabf.asi_features.length) {
            let bad = false;
            // console.log(cabf);
            // console.log(choice);
            if (log_it) {
              for (const caf of cabf.asi_features) {
                console.log(caf.selected_option);
                console.log(caf.true_id);
              }
              for (const caf of choice.asi_features) {
                console.log(caf.true_id);
              }
            }
            for (const caf of cabf.asi_features) {
              if (choice.asi_features.filter(o => o.true_id === caf.true_id).length === 0) {
                bad = true;
                break;
              }
            }
            // console.log(bad);
            if (bad) {
              // It's possible that the true_ids are wrong, but otherwise everything is fine.  
              // See if the current ones can work with the correct ones.  
              // If so, change their true_ids.
              let used_true_ids: string[] = [];
              bad = false;
              for (const caf of cabf.asi_features) {
                if (choice.asi_features.filter(o => o.true_id === caf.true_id).length === 0) {
                  // Look for one that can work otherwise
                   
                  const af = choice.asi_features.find(o => 
                    o.amount === caf.amount && o.options.includes(caf.selected_option) && !used_true_ids.includes(o.true_id));
                  // console.log(af);
                  if (af) {
                    caf.true_id = af.true_id;
                    used_true_ids.push(af.true_id);
                  } else {
                    bad = true;
                    break;
                  }
                }
              }
              if (log_it && bad) {
                for (const caf of cabf.asi_features) {
                  console.log(caf.selected_option);
                  console.log(caf.true_id);
                }
                for (const caf of choice.asi_features) {
                  console.log(caf.true_id);
                }
              }
              if (bad && char instanceof Character && char.character_options.asi_flexibility) {
                // It's possible that we're using flexible.
                bad = false;
                used_true_ids = [];
                for (const caf of cabf.asi_features) {
                  if (choice.asi_features.filter(o => o.true_id === caf.true_id).length === 0) {
                    // Look for one that can work otherwise
                     
                    const af = choice.asi_features.find(o => o.amount === caf.amount && !used_true_ids.includes(o.true_id));
                    // console.log(af);
                    if (af) {
                      caf.true_id = af.true_id;
                      used_true_ids.push(af.true_id);
                    } else {
                      bad = true;
                      break;
                    }
                  }
                }
              }
            }
            if (bad) {
              rebuild();
            }
          } else {
            rebuild();
          }
        }
      } else if (copyMe.feature_type.includes("Condition")) {
      } else if (copyMe.feature_type.includes("Special Feature") && copyMe.feature_type !== "Special Feature List Merge") {
        // Handled in repopulate
      } else if (copyMe.feature_type.includes("Special Feature List Merge")) {
        // Nothing necessary here.  It only needs the feature to be attached
      } else if (copyMe.feature_type === "Feat") {
        if (this.feature_options.length === 0) {
          const char_feat = new CharacterFeat();
          // if (char && copyMe.floating_feature instanceof FeatFeature && 
          //   copyMe.floating_feature.feat_ids.length === 1 && 
          //   !copyMe.floating_feature.feat_ids.includes("Any") &&
          //   !char.feat_ids.includes(copyMe.floating_feature.feat_ids[0])) {
          //   const id = copyMe.floating_feature.feat_ids[0];
          //   const objFinder = all_feature_sources.filter(o => o._id === id);
          //   let feat: FeatureSource | null = null;
          //   if (objFinder.length === 1) {
          //     feat = objFinder[0];
          //     char_feat.copy_source(char, feat, "", "", all_feature_sources, [], []);
          //     char.feat_ids.push(id);
          //   }
          // }
          this.feature_options.push(char_feat);
        }
      } else if (copyMe.feature_type === "Selectable" && copyMe.floating_feature instanceof Selectable) {
        const selectable = copyMe.floating_feature;
        const source_id = parent instanceof CharacterFeatureBase ? parent.source_true_id : "";
        const count = char instanceof Character ? char_util.value(selectable.count, { obj: char, source_id }) : 1;
        // TODO: Artificer UA is going to need to come here for type === "Item", and it's going to have a variable count
        if (selectable.type === "Spell" || selectable.type === "Item") {
          // debugger;
          if (this.feature_options.length !== count) {
            while (this.feature_options.length < count) {
              this.feature_options.push("");
            }
          }
        } else {
          if (this.feature_options.length !== 1) {
            this.feature_options = [""];
          }
        }
      } else if (copyMe.feature_type === "Eldritch Invocations") {
        // Handled in char_util now.
        // if (this.feature_options.length === 0) {
        //   // I probably need to change this to work more like special features
        //   this.feature_options.push(new CharacterEldritchInvocation());
        // }
      } else if (copyMe.feature_type === "Weapon Mastery") {
        // This is from an old way of handling weapon mastery. The new way doesn't use it.
        // if (this.floating_feature instanceof WeaponMasteryFeature) {
        //   for (const cwm of (this.feature_options as CharacterWeaponMastery[])) {
        //     cwm.wm_ff = this.floating_feature;
        //   }
        // }
      } else if (copyMe.feature_type === "Fighting Style") {
        if (this.feature_options.length === 0) {
          // I probably need to change this to work more like special features
          this.feature_options.push(new CharacterFightingStyle());
        }
      } else if (copyMe.feature_type === "Pact Boon") {
        if (this.feature_options.length === 0) {
          // I probably need to change this to work more like special features
          this.feature_options.push(new CharacterPactBoon());
        }
      } else if (copyMe.feature_type === "Characteristic") {
        if (this.feature_options.length === 0) {
          this.feature_options.push("");
        }
      } else if (copyMe.feature_type === "Subclass") {
        if (this.feature_options.length === 0) {
          // I probably need to change this to work more like special features
          this.feature_options.push(new CharacterSubclass());
        }
      } else if (copyMe.feature_type === "Subrace") {
        if (this.feature_options.length === 0) {
          // I probably need to change this to work more like special features
          this.feature_options.push(new CharacterSubrace());
        }
      } else if (copyMe.feature_type === "Sense") {
        // console.log(copyMe);
        // console.log(this.feature);
      } else if (copyMe.feature_type === "Proficiencies") {
        const prof = copyMe.floating_feature as Proficiency;
        // if (copyMe.floating_feature instanceof Proficiency && copyMe.floating_feature.proficiency_types.includes("Saving Throw")) {
        //   console.log(copyMe);
        //   console.log(this);
        //   debugger;
        // }
        const auto_choose = (prof.the_proficiencies.filter(o => 
          ["Gaming Set", "Artisan's Tools", "Musical Instrument", 
            "Specialty Tool", "Other Tool"].includes(o.proficiency_id)).length === 0 && 
          prof.choice_count === prof.the_proficiencies.length);
        if (this.feature_options.length !== prof.choice_count || (auto_choose && this.feature_options.find(o => o.proficiency_id === ""))) {
          this.feature_options = [];
          for (let i = 0; i < prof.choice_count; ++i) {
            const char_prof: CharacterProficiency = {
              prof_feature_id: this.true_id,
              expertise_feature_id: "",
              proficiency_id: (auto_choose ? prof.the_proficiencies[i].proficiency_id : ""),
              proficiency_level: prof.double ? 2 : 1,
              type: "",
              extra_bonus: 0
            };
            this.feature_options.push(char_prof);
          }
        }
      } else if (copyMe.feature_type === "Expertise") {
        // This is where the problem is that's clearing out the expertise
        // if (this.feature_options.length !== (copyMe.floating_feature as NumberFeature).num) {
        //   this.feature_options = [];
        //   const expertises = copyMe.floating_feature as NumberFeature;
        //   for (let i = 0; i < expertises.num; i++) {
        //     const char_prof: CharacterProficiency = {
        //       prof_feature_id: "",
        //       expertise_feature_id: copyMe.true_id,
        //       proficiency_id: "",
        //       proficiency_level: 2,
        //       type: "Expertise"
        //     };
        //     this.feature_options.push(char_prof);
        //   }
        // }
      } else if (["Spellbook","Spellcasting","Spell List","Ritual Casting",
        "Cantrips","Spells","Mystic Arcanum","Spell Mastery"].includes(copyMe.feature_type)) {
        if (this.feature_options.length === 0) {
          const spellcasting = new CharacterSpellcasting();
          spellcasting.copyFeature(this);
          this.feature_options.push(spellcasting);
        }
      } else if (copyMe.feature_type === "Spells from List") {
        // Handled Elsewhere
      } else if (copyMe.feature_type === "Bonus Spells") {
        if (this.feature_options.length === 0) {
          const char_bs = new CharacterBonusSpells();
          char_bs.cf_id = this.true_id;
          char_bs.ff_id = copyMe.floating_feature_id;
          if (copyMe.floating_feature instanceof BonusSpells) {
            char_bs.ff = copyMe.floating_feature;
          }
          this.feature_options.push(char_bs);
        } else {
          // Make sure it has the ids in there properly
          const char_bs = this.feature_options[0];
          if (char_bs instanceof CharacterBonusSpells) {
            char_bs.cf_id = this.true_id;
            char_bs.ff_id = copyMe.floating_feature_id;
          }
        }
      } else if (copyMe.feature_type === "Knowledge") {
        // Handled in repopulate
      } else {
        // console.log(copyMe);
      }
    }
  }

  get floating_feature(): FloatingFeature | null {
    if (this.feature) {
      return this.feature.floating_feature;
    }
    return null;
  }

  clone(drop_ids = false): CharacterFeature {
    const clone = new CharacterFeature();
    clone.copy(this, drop_ids);
    return clone;
  }

  copy(copyMe: CharacterFeature, drop_ids = false): void {
    if (!drop_ids) {
      this.true_id = copyMe.true_id;
    }
    this.feature_id = copyMe.feature_id;
    this.source_id = copyMe.source_id;
    this.source_type = copyMe.source_type;
    this.source_name = copyMe.source_name;
    this.creature_fb_type = copyMe.creature_fb_type;
    // this.root_id = copyMe.root_id;
    // this.root_type = copyMe.root_type;
    // this.root_name = copyMe.root_name;
    this.ancestries = [...copyMe.ancestries.map(o => { return {...o}; })];
    this.item_id = copyMe.item_id;
    this.item_name = copyMe.item_name;
    this.name = copyMe.name;
    this.feature_type = copyMe.feature_type;
    this.feature = copyMe.feature;
    this.special_resource = copyMe.special_resource;
    this.special_resource_max = copyMe.special_resource_max;
    this.feature_options = [...copyMe.feature_options];
  }
}

export class CFFeatureSource extends CharacterFeature {
  
}

export class CFProficiency extends CharacterFeature {

}